/**
 * 마크다운 + LaTeX(KaTeX) 공용 렌더.
 * 답변 본문(response-renderer)과 Citation 의 KB 문서 조각이 같이 쓴다.
 */
import React, { useLayoutEffect, useRef } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import katex from "katex";

/** 인라인 수식 스켈레톤 */
export const MathInlineSkeleton = () => (
  <span className="inline-block align-middle rounded px-2 py-[2px] text-base font-bold text-grey-40 animate-pulse">
    [Rendering equation...]
  </span>
);

/** 블록 수식 스켈레톤 */
export const MathBlockSkeleton = () => (
  <div className="my-4 rounded-lg px-3 py-2 text-base font-bold text-grey-20 animate-pulse">
    [Rendering equation...]
  </div>
);

/** katex 직접 렌더링 — 실패(미완성 수식) 시 스켈레톤 반환 */
export function renderMath(formula: string, displayMode: boolean): React.ReactNode {
  try {
    const html = katex.renderToString(formula, {
      displayMode,
      throwOnError: true,
      strict: false,
    });
    if (displayMode) {
      return (
        <div
          className="overflow-x-auto my-4"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      );
    }
    return <span dangerouslySetInnerHTML={{ __html: html }} />;
  } catch {
    return displayMode ? <MathBlockSkeleton /> : <MathInlineSkeleton />;
  }
}

/** 코드 블록 내용이 LaTeX처럼 생겼는지 판단 */
export function looksLikeMath(code: string): boolean {
  return (
    code.includes("\\begin{") ||
    code.includes("\\frac") ||
    code.includes("\\int") ||
    code.includes("\\sum") ||
    code.includes("\\prod") ||
    code.includes("\\sqrt") ||
    code.includes("\\alpha") ||
    code.includes("\\beta") ||
    code.includes("\\gamma") ||
    /\\[a-zA-Z]+[_{^]/.test(code)
  );
}

/**
 * 백틱으로 감싼 수식을 KaTeX 로 렌더하는 code/pre 컴포넌트.
 * AI가 수식을 백틱으로 감싸서 출력하는 경우 처리. ReactMarkdown 의 components 에 펼쳐 쓴다.
 */
export const mathCodeComponents = {
  /** 인라인 코드(`...`)가 $$...$$ 또는 $...$ 수식이면 KaTeX로 렌더링 */
  code: ({ className, children }: { className?: string; children?: React.ReactNode }) => {
    const content = String(children ?? "").trim();
    if (!className) {
      // $$...$$ → 블록 수식
      const blockMatch = content.match(/^\$\$([\s\S]+)\$\$$/);
      if (blockMatch) return renderMath(blockMatch[1].trim(), true);
      // $...$ → 인라인 수식
      const inlineMatch = content.match(/^\$((?:[^$]|\\\$)+)\$$/);
      if (inlineMatch) return renderMath(inlineMatch[1].trim(), false);
    }
    return <code className={className}>{children}</code>;
  },
  /** 코드 블록(``` ```)이 LaTeX처럼 보이면 KaTeX로 렌더링, 실패 시 스켈레톤 */
  pre: ({ children }: { children?: React.ReactNode }) => {
    const codeEl = React.Children.toArray(children).find(
      (c): c is React.ReactElement => React.isValidElement(c) && c.type === "code"
    );
    if (codeEl) {
      const raw = String(codeEl.props.children ?? "").trim();
      if (looksLikeMath(raw)) {
        return renderMath(raw, true);
      }
    }
    return <pre>{children}</pre>;
  },
};

/* 문서 조각은 외부 입력이다. 이미지는 펼치는 순간 외부로 요청이 나가므로 대체 글자로만 보이고,
   링크는 현재 탭을 바꾸지 않게 새 탭으로 연다 */
const MARKDOWN_MATH_COMPONENTS = {
  img: ({ alt }: { alt?: string }) => <span>{alt ?? ""}</span>,
  a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
};

/**
 * 마크다운 + 수식 래퍼. `$...$`, 자체 줄의 `$$...$$` 를 KaTeX 로 그린다.
 * 넓은 블록 수식은 가로 스크롤 대신 폭에 맞춰 줄인다.
 * 백틱 수식 변환은 하지 않는다. 문서 조각의 코드 예시가 수식으로 바뀌어 깨지기 때문이다.
 */
export const MarkdownMath = ({
  children,
  className,
}: {
  children: string;
  className?: string;
}) => {
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const root = ref.current;
    if (!root) return;
    const fit = () => {
      root.querySelectorAll<HTMLElement>(".katex-display").forEach((disp) => {
        const inner = disp.querySelector<HTMLElement>(".katex");
        if (!inner) return;
        inner.style.transform = "";
        inner.style.transformOrigin = "left center";
        inner.style.display = "inline-block";
        const avail = disp.clientWidth;
        const need = inner.scrollWidth;
        if (avail > 0 && need > avail) {
          inner.style.transform = `scale(${avail / need})`;
        }
      });
    };
    fit();
    // KaTeX 폰트 적용 직후 폭이 바뀔 수 있어 다음 프레임에 한 번 더 맞춘다
    const raf = requestAnimationFrame(fit);
    return () => cancelAnimationFrame(raf);
  }, [children]);

  return (
    <div ref={ref} className={className}>
      <ReactMarkdown
        remarkPlugins={[[remarkGfm, { singleTilde: false }], remarkMath]}
        rehypePlugins={[rehypeKatex]}
        components={MARKDOWN_MATH_COMPONENTS}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
};
